import { RecordingDownloadPopover as SharedRecordingDownloadPopover } from '@ouster/webviz-react-components';
import { useCallback, useEffect, useState } from 'react';

import useChakraHook from '../../hooks/useChakraHook';
import { useDownloadSliceOsf } from '../../hooks/useDownloadSliceOsf';
import { useStoreActions, useStoreState } from '../../reducers';
import { FileInfo, OutputFileInfo } from '../../types';
import {
  BASE_ORG_API,
  BASE_PUBLISHED_RECORDING_API,
} from '../../utils/constants';
import { fetchWithAuth } from '../../utils/requests';

type RecordingDownloadPopoverProps = {
  /**
   * Null while the popover is closed. This component stays mounted either
   * way (see the slice hook below), so it drives visibility rather than
   * the caller unmounting us.
   */
  anchorEl: HTMLElement | null;
  closeDownloadMenu: () => void;
  downloadRecordingId: string;
  orgId?: number;
  isPublic?: boolean;
  recordingTitle?: string;
  isSlamProcessing?: boolean;
};

export default function RecordingDownloadPopover({
  anchorEl,
  closeDownloadMenu,
  downloadRecordingId,
  orgId,
  isPublic = false,
  recordingTitle,
  isSlamProcessing = false,
}: RecordingDownloadPopoverProps) {
  const isOpen = Boolean(anchorEl);
  const [isLoadingDownloadInfo, setIsLoadingDownloadInfo] = useState(false);
  const { loadSourceFileInfo, loadOutputFileInfo } = useStoreActions(
    (actions) => actions.drive
  );
  const { showToast } = useChakraHook();
  const {
    outputForDownload,
    outputForDownloadNonSlam,
    sourceDataForDownload,
    sourceConfigForDownload,
  } = useStoreState((storeState) => storeState.drive);
  const downloadableOutputFiles = [
    ...outputForDownload,
    ...outputForDownloadNonSlam,
  ];

  useEffect(() => {
    const loadDownloadInfo = async () => {
      try {
        setIsLoadingDownloadInfo(true);
        await loadOutputFileInfo({
          id: downloadRecordingId,
          isPrivate: !isPublic,
          orgId,
        });

        await loadSourceFileInfo({
          id: downloadRecordingId,
          isPrivate: !isPublic,
          orgId,
        });
      } catch (error) {
        // console.error('Error loading download info:', error);
        showToast({
          title: 'Failed to download recording',
          description:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred',
          status: 'error',
        });
        closeDownloadMenu();
      } finally {
        setIsLoadingDownloadInfo(false);
      }
    };

    // Gated on the popover being open, not just on which recording is
    // selected: this component stays mounted across open/close (so a slice
    // outlives dismissal), while the pages clear the store's file listings
    // on close. Keyed on the id alone, reopening the same recording would
    // skip the effect and show an empty popover.
    if (isOpen && orgId && downloadRecordingId) loadDownloadInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    downloadRecordingId,
    isPublic,
    loadOutputFileInfo,
    loadSourceFileInfo,
    orgId,
    showToast,
  ]);

  const getFileDownloadUrl = useCallback(
    async (
      driveUuid: string,
      file: FileInfo | OutputFileInfo,
      type: string,
      downloadFilename?: string
    ) => {
      const downloadPath = isPublic
        ? `${BASE_PUBLISHED_RECORDING_API}/${driveUuid}/${type}/files/${file.id}/signed_url`
        : `${BASE_ORG_API}/${orgId}/recordings/${driveUuid}/${type}/files/${file.id}/signed_url`;

      const response = await fetchWithAuth(downloadPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          downloadFilename: file.filename ?? downloadFilename,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Something went wrong when creating signed URL for file ${file.id}`
        );
      }

      return response.json() as Promise<{ url: string }>;
    },
    [isPublic, orgId]
  );

  // Owned here rather than inside the shared popover component: that one
  // is torn down the moment the popover closes, and a slice outlives that
  // by tens of seconds - state kept in there would take the in-flight
  // job's progress and its download-on-completion with it.
  const sliceOsf = useDownloadSliceOsf({
    driveUuid: downloadRecordingId,
    orgId,
    isPublic,
    downloadableOutputFiles,
    recordingTitle,
    isOpen,
  });

  return (
    <SharedRecordingDownloadPopover
      anchorEl={anchorEl}
      closeDownloadMenu={closeDownloadMenu}
      driveUuid={downloadRecordingId}
      sourceDataForDownload={sourceDataForDownload}
      sourceConfigForDownload={sourceConfigForDownload}
      downloadableOutputFiles={downloadableOutputFiles}
      isSlamProcessing={isSlamProcessing}
      getFileDownloadUrl={getFileDownloadUrl}
      isLoadingDownloadInfo={isLoadingDownloadInfo}
      recordingTitle={recordingTitle}
      sliceOsf={sliceOsf}
    />
  );
}
